/**
 * SceneFrame — the single wrapper every 3D section uses.
 *
 * It enforces the performance contract:
 *  - the canvas module is React.lazy + Suspense, so `three` lives in its own async
 *    chunk that never loads on routes without a scene;
 *  - it mounts only when an IntersectionObserver reports the section within 200px
 *    of the viewport, and unmounts (disposing the renderer) once the section is
 *    more than one viewport height away;
 *  - a fixed-dimension WebP poster paints immediately (CLS = 0) and the canvas
 *    cross-fades in over 400ms once its first frame is ready;
 *  - graceful degradation to poster-only for reduced-motion, no WebGL2 and
 *    Save-Data; reduced particle counts and frameloop="demand" on low-power
 *    devices;
 *  - the canvas carries role="img" and a full aria-label.
 */
import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useCapability } from './capability';
import { register, setNear, scheduleEvaluate } from './sceneSlots';

export default function SceneFrame({
  load,
  poster,
  posterAlt,
  ariaLabel,
  className = '',
  frameClassName = '',
  style,
  children,
  sceneProps = {},
  rounded = true,
  overlay = null,
}) {
  const capability = useCapability();
  const containerRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [firstFrame, setFirstFrame] = useState(false);
  // Bumped when a WebGL context is lost, which remounts the lazy scene cleanly.
  const [recoveries, setRecoveries] = useState(0);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || capability.posterOnly) return undefined;

    // The slot manager decides which of the page's scenes may hold a live WebGL
    // context, so four scenes on one page can never exhaust the device.
    const unregister = register(node, (granted) => {
      setMounted(granted);
      if (!granted) setFirstFrame(false);
    });

    const near = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
        setNear(node, entry.isIntersecting);
      },
      { rootMargin: '200px 0px 200px 0px', threshold: 0 }
    );
    near.observe(node);
    scheduleEvaluate();
    return () => {
      near.disconnect();
      unregister();
    };
  }, [capability.posterOnly]);

  const LazyScene = useMemo(() => (load ? React.lazy(load) : null), [load]);

  /**
   * react-three-fiber inserts its own wrapper divs between our labelled element
   * and the <canvas>, so the role/aria-label are also written straight onto the
   * canvas element. Every scene is then unambiguously exposed as an image with a
   * description, however a screen reader walks the tree.
   */
  const canvasHostRef = useRef(null);
  useEffect(() => {
    if (!mounted) return undefined;
    const host = canvasHostRef.current;
    if (!host) return undefined;
    const seen = new WeakSet();
    const onLost = (event) => {
      // A lost context never repaints, so drop the canvas and let the slot
      // manager grant it again on the next pass rather than leaving a dead frame.
      event.preventDefault?.();
      setFirstFrame(false);
      setRecoveries((count) => count + 1);
    };
    const apply = () => {
      host.querySelectorAll('canvas').forEach((canvas) => {
        canvas.setAttribute('role', 'img');
        if (ariaLabel) canvas.setAttribute('aria-label', ariaLabel);
        if (!seen.has(canvas)) {
          seen.add(canvas);
          canvas.addEventListener('webglcontextlost', onLost);
        }
      });
    };
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(host, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [mounted, ariaLabel, firstFrame]);

  return (
    <div
      ref={containerRef}
      className={`scene-frame ${rounded ? 'scene-frame--rounded' : ''} ${className}`}
      style={style}
      data-testid="scene-frame"
      data-scene-state={
        capability.posterOnly ? 'poster-only' : mounted ? (firstFrame ? 'live' : 'loading') : 'idle'
      }
    >
      {poster ? (
        <img
          src={poster}
          alt={posterAlt || ''}
          aria-hidden={posterAlt ? undefined : 'true'}
          width={1600}
          height={900}
          decoding="async"
          className={`scene-frame__poster ${firstFrame ? 'is-hidden' : ''}`}
        />
      ) : (
        <div className="scene-frame__poster scene-frame__poster--fallback" aria-hidden="true" />
      )}

      {!capability.posterOnly && mounted && LazyScene ? (
        <div
          key={recoveries}
          ref={canvasHostRef}
          className={`scene-frame__canvas ${firstFrame ? 'is-visible' : ''} ${frameClassName}`}
          role="img"
          aria-label={ariaLabel}
        >
          <Suspense fallback={null}>
            <LazyScene
              active={inView}
              capability={capability}
              onFirstFrame={() => setFirstFrame(true)}
              {...sceneProps}
            />
          </Suspense>
        </div>
      ) : null}

      {overlay}
      {children}
    </div>
  );
}
